import { css } from "@emotion/react";
import { Theme, useTheme } from "@mui/material";
import Link from "next/link";
import { useCallback, useState } from "react";
import Modal from "react-modal";

import { QuestVerificationFaq } from "~/components/quest/QuestVerificationFaq";
import { CompletedModalContent } from "~/components/quest/detail/CompletedModalContent";
import { QuestTaskView } from "~/components/quest/detail/QuestTaskView";
import {
  Props,
  useConvertMarkdownToHtml,
} from "~/components/quest/page/[id]/lib";
import {
  getQuestDurationStatus,
  questDurationLabel,
} from "~/domains/quest/lib";
import VerifyCode from "~/extensions/instant-verify-checker/verify-code";
import {
  mediaQueryIsDesktop,
  mediaQueryIsMobile,
  wrapLongTextStyle,
} from "~/lib/client/design";
import { engFont } from "~/lib/client/fonts";
import { formatDateTime } from "~/lib/graphql/graphqlClient";
import "~/lib/monkeypatch";

const QuestDetailPage = ({ quest }: Props) => {
  const theme = useTheme();
  const questDescription = useConvertMarkdownToHtml(quest?.description || "");

  const [completedByQuestTaskId, setCompletedByQuestTaskId] = useState(
    quest.questTasks
      .map((t) => ({
        id: t.id,
        completed: false,
      }))
      .indexById()
  );
  const updateCompletedByQuestTaskId = useCallback((questTaskId: number) => {
    console.time(`updated: ${questTaskId}`);
    setCompletedByQuestTaskId((prev) => ({
      ...prev,
      [questTaskId]: {
        ...prev[questTaskId],
        completed: true,
      },
    }));
  }, []);
  const completed = Object.values(completedByQuestTaskId).every(
    (v) => v.completed
  );

  // 検定に紐づくクエストかどうかを判定
  const hasExams = quest.externalExams && quest.externalExams.length > 0;

  return (
    <main css={rootStyle}>
      <i css={durationLabelStyle(theme)} className={engFont.className}>
        {questDurationLabel(getQuestDurationStatus(quest))}
      </i>
      <h1>{quest.title}</h1>
      <p css={questPublisherNameStyle(theme)}>
        発行者：{quest.user.name}
        <br />
        期間：{formatDateTime(quest.startAt)}~{formatDateTime(quest.endAt)}
      </p>
      <div
        css={questDescriptionStyle(theme)}
        dangerouslySetInnerHTML={{ __html: questDescription }}
      />
      <QuestVerificationFaq />
      <VerifyCode quest={quest} />
      {quest.questTasks.map((task) => (
        <QuestTaskView
          quest={quest}
          questTask={task}
          setCompleteQuestTask={updateCompletedByQuestTaskId}
          key={task.id}
        />
      ))}
      <div css={linkContainerStyle(theme)}>
        <Link href={`/category/${quest.category.id}`}>
          {`▼ 「${quest.category.name}」の他のクエストを見る`}
        </Link>
      </div>
      <Modal
        isOpen={completed}
        onRequestClose={() => {}}
        style={{
          content: {
            top: "50%",
            left: "50%",
            right: "auto",
            bottom: "auto",
            marginRight: "-50%",
            transform: "translate(-50%, -60%)",
            height: hasExams ? "auto" : "75dvh",
            maxHeight: hasExams ? "60dvh" : "75dvh",
            width: hasExams ? "auto" : "auto",
            maxWidth: hasExams ? "90vw" : "none",
            border: "4px solid #ccc",
            borderRadius: "1rem",
            padding: "0",
            backgroundColor: theme.palette.background.default,
            overflow: hasExams ? "visible" : "auto",
          },
        }}
      >
        <CompletedModalContent category={quest.category} quest={quest} />
      </Modal>
    </main>
  );
};

const rootStyle = css`
  ${mediaQueryIsDesktop(css`
    padding: 0 64px;
    max-width: 1200px;
    margin: 0 auto;
  `)};

  ${mediaQueryIsMobile(css`
    padding: 0 16px;

    h1 {
      font-size: 1.5rem;
      ::after {
        content: "";
        display: block;
        width: 100%;
        height: 1px;
        background-color: #ccc;
      }
    }
  `)};
`;

const durationLabelStyle = (theme: Theme) => css`
  width: fit-content;
  color: ${theme.palette.primary.main};
  border: 1px solid ${theme.palette.primary.main};
  border-radius: 8px;
  padding: 4px 8px;
  font-size: 0.8rem;

  margin: 1rem 0;
  display: block;
`;

const questPublisherNameStyle = (theme: Theme) => css`
  background: ${theme.palette.background.paper};

  margin-top: 0;
  margin-bottom: 32px;
  padding: 0.5rem;
  border-radius: 4px;
`;

const questDescriptionStyle = (theme: Theme) => css`
  ${mediaQueryIsDesktop(css`
    img {
      max-width: 80%;
      height: auto;

      // 中央寄せ
      display: block;
      margin: 0 auto;
    }
  `)}

  ${mediaQueryIsMobile(css`
    img {
      max-width: 100%;
      height: auto;
    }
  `)}

  p {
    margin: 0;
  }

  ul {
    margin-top: -0.5rem;
    margin-bottom: -0.5rem;
  }

  li {
    margin-top: -0.5rem;
    margin-bottom: -0.5rem;
  }

  h3 {
    margin-top: -0.5rem;
    margin-bottom: -0.5rem;

    ::after {
      content: "";
      display: block;
      width: 100%;
      height: 1px;
      background-color: #ccc;
      margin-top: 0.25rem;
    }
  }

  a {
    color: ${theme.palette.primary.light};
    text-decoration: underline;

    &:hover {
      color: ${theme.palette.primary.dark};
    }

    &:visited {
      color: ${theme.palette.primary.dark};
    }
  }

  ${wrapLongTextStyle};
`;

const linkContainerStyle = (theme: Theme) => css`
  margin-top: 5rem;
  text-align: right;
  font-size: 0.8rem;

  a {
    color: ${theme.palette.primary.light};
    text-decoration: underline;
  }
`;

export default QuestDetailPage;
